<template>
  <div class="min-h-screen bg-neutral-50 pt-8 pb-16">
    <div class="container mx-auto px-4 sm:px-6 lg:px-8">
      <!-- 标题 -->
      <div class="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
        <div>
          <h1 class="text-2xl md:text-3xl font-bold text-neutral-900">
            我的 HTML 工作台
          </h1>
          <p class="text-sm text-neutral-500 mt-1">
            导入你自己的网页（AI 生成的看板、系统都行），站内直接打开；数据按账号隔离，每人各一份。
          </p>
        </div>
        <div class="flex gap-2">
          <button
            class="px-4 py-2 rounded-lg border border-neutral-200 text-sm text-neutral-700 hover:border-primary hover:text-primary transition-colors"
            @click="openEditor(null, sampleHtml())"
          >
            <i class="fa fa-magic mr-1"></i>用示例创建
          </button>
          <button
            class="px-4 py-2 rounded-lg bg-primary text-white text-sm hover:opacity-90 transition-opacity"
            @click="openEditor(null)"
          >
            <i class="fa fa-plus mr-1"></i>新建工作台
          </button>
        </div>
      </div>

      <!-- 列表 -->
      <div v-if="loading" class="py-16 text-center text-sm text-neutral-400">
        <i class="fa fa-spinner fa-spin mr-2"></i>加载中…
      </div>
      <div
        v-else-if="!list.length"
        class="bg-white rounded-2xl border border-dashed border-neutral-300 py-16 text-center"
      >
        <i class="fa fa-window-maximize text-3xl text-neutral-300"></i>
        <p class="mt-3 text-sm text-neutral-500">
          还没有工作台，点右上角新建，导入一个 .html 文件即可
        </p>
      </div>
      <div v-else class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <div
          v-for="w in list"
          :key="w.id"
          class="bg-white rounded-2xl border border-neutral-200 p-5 flex flex-col"
        >
          <div class="flex items-start justify-between gap-2">
            <div class="min-w-0">
              <h3 class="text-base font-bold text-neutral-900 truncate">
                {{ w.name }}
              </h3>
              <p class="text-xs text-neutral-400 mt-1">
                {{ fmtTime(w.updateTime) }} · {{ fmtSize(w.htmlSize) }}
              </p>
            </div>
            <span
              v-if="w.isPublic"
              class="px-2 py-0.5 text-[11px] rounded-full flex-shrink-0"
              style="background: #ecfdf5; color: #047857"
              >已公开</span
            >
          </div>
          <div class="flex gap-2 mt-4">
            <button
              class="flex-1 px-3 py-2 rounded-lg bg-primary text-white text-xs hover:opacity-90"
              @click="openRun(w)"
            >
              <i class="fa fa-play mr-1"></i>打开
            </button>
            <button
              class="px-3 py-2 rounded-lg border border-neutral-200 text-xs text-neutral-600 hover:border-primary hover:text-primary"
              @click="editItem(w)"
            >
              编辑
            </button>
            <button
              class="px-3 py-2 rounded-lg border border-neutral-200 text-xs text-neutral-600 hover:border-primary hover:text-primary"
              @click="toggleShare(w)"
            >
              {{ w.isPublic ? "取消公开" : "公开" }}
            </button>
            <button
              class="px-3 py-2 rounded-lg border border-neutral-200 text-xs text-neutral-400 hover:border-red-300 hover:text-red-500"
              @click="removeItem(w)"
            >
              删除
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 编辑器 -->
    <el-dialog
      v-model="editorVisible"
      :title="form.id ? '编辑工作台' : '新建工作台'"
      width="920px"
      top="4vh"
      destroy-on-close
    >
      <div class="space-y-4">
        <div class="flex flex-col md:flex-row gap-4">
          <div class="flex-1">
            <label class="block text-xs text-neutral-500 mb-1">工作台名称</label>
            <el-input v-model="form.name" maxlength="60" placeholder="如：班主任工作台" />
          </div>
          <div class="w-full md:w-52">
            <label class="block text-xs text-neutral-500 mb-1">主题色</label>
            <el-color-picker v-model="form.accent" />
          </div>
          <div class="w-full md:w-40">
            <label class="block text-xs text-neutral-500 mb-1">显示侧栏</label>
            <el-switch v-model="form.sidebar" />
          </div>
        </div>

        <div>
          <div class="flex items-center justify-between mb-1">
            <label class="text-xs text-neutral-500">HTML 源码</label>
            <div class="flex items-center gap-2">
              <input
                ref="fileInput"
                type="file"
                accept=".html,.htm,text/html"
                class="hidden"
                @change="onFile"
              />
              <button
                class="px-3 py-1 rounded-lg border border-neutral-200 text-xs text-neutral-600 hover:border-primary hover:text-primary"
                @click="$refs.fileInput.click()"
              >
                <i class="fa fa-upload mr-1"></i>导入 .html 文件
              </button>
            </div>
          </div>
          <el-input
            v-model="form.html"
            type="textarea"
            :rows="14"
            placeholder="粘贴完整 HTML（含 <html>/<head>/<style>/<script>），或点上方导入文件"
            class="font-mono"
          />
          <p class="text-[11px] text-neutral-400 mt-1">
            页面脚本请用 <code>window.WB.onReady(fn)</code> 等数据加载完再渲染，
            内部用 <code>WB.get(key, 默认值)</code> / <code>WB.set(key, 值)</code>
            读写本账号数据（跨设备一致）。页面里的
            <code>localStorage</code>
            也会按「工作台 + 账号」自动隔离，别人打开同一个工作台看不到你的数据。
          </p>
        </div>
      </div>

      <template #footer>
        <button
          class="px-4 py-2 rounded-lg border border-neutral-200 text-sm text-neutral-600 mr-2"
          @click="editorVisible = false"
        >
          取消
        </button>
        <button
          class="px-4 py-2 rounded-lg bg-primary text-white text-sm disabled:opacity-60"
          :disabled="saving"
          @click="save"
        >
          {{ saving ? "保存中…" : "保存" }}
        </button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from "element-plus";
import { requireLoginPage, isLogin } from "@/utils/authGuard";

export default {
  name: "WorkbenchPage",
  data() {
    return {
      loading: false,
      saving: false,
      list: [],
      editorVisible: false,
      form: {
        id: "",
        name: "",
        html: "",
        accent: "#3b82f6",
        sidebar: true,
      },
    };
  },
  mounted() {
    if (!requireLoginPage(this, "我的 HTML 工作台")) {
      return;
    }
    this.load();
  },
  methods: {
    load() {
      this.loading = true;
      this.$axios({ url: "user/workbench/mine" })
        .then((res) => {
          if (res.code === 200 && res.data) {
            this.list = res.data.list || [];
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    editItem(w) {
      this.$axios({ url: "user/workbench/detail", params: { id: w.id } }).then(
        (res) => {
          if (res.code === 200 && res.data && res.data.workbench) {
            const d = res.data.workbench;
            let cfg = {};
            try {
              cfg = JSON.parse(d.config || "{}");
            } catch (e) {
              cfg = {};
            }
            this.openEditor(
              { id: d.id, name: d.name },
              d.html,
              cfg.accent,
              cfg.sidebar
            );
          }
        }
      );
    },
    openEditor(item, html, accent, sidebar) {
      this.form = {
        id: (item && item.id) || "",
        name: (item && item.name) || "",
        html: html || "",
        accent: accent || "#3b82f6",
        sidebar: typeof sidebar === "boolean" ? sidebar : true,
      };
      if (!this.form.name && !this.form.id) {
        this.form.name = "";
      }
      this.editorVisible = true;
    },
    onFile(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      if (file.size > 2 * 1024 * 1024) {
        ElMessage.warning("HTML 文件请控制在 2MB 以内");
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        this.form.html = String(reader.result || "");
        if (!this.form.name) {
          this.form.name = file.name.replace(/\.html?$/i, "");
        }
      };
      reader.readAsText(file, "utf-8");
      e.target.value = "";
    },
    async save() {
      if (!isLogin()) {
        requireLoginPage(this, "保存工作台");
        return;
      }
      if (!this.form.html.trim()) {
        ElMessage.warning("请先导入或粘贴 HTML");
        return;
      }
      const ext = this.scanExternal(this.form.html);
      if (ext.length) {
        try {
          await ElMessageBox.confirm(
            `检测到 ${ext.length} 个外部脚本/样式引用（如 ${ext[0].slice(
              0,
              60
            )}）。这类页面在站内可能白屏，建议在源工具里导出「单文件 HTML（资源内联）」后再导入。仍要保存吗？`,
            "HTML 不是自包含的",
            {
              confirmButtonText: "仍然保存",
              cancelButtonText: "我去内联",
              type: "warning",
            }
          );
        } catch (e) {
          return;
        }
      }
      this.saving = true;
      try {
        const config = JSON.stringify({
          accent: this.form.accent,
          sidebar: this.form.sidebar,
          title: this.form.name,
          theme: "light",
        });
        const data = await this.$aesEncrypt({
          id: this.form.id,
          name: this.form.name,
          html: this.form.html,
          config,
        });
        const res = await this.$axios({
          url: "user/workbench/save",
          method: "POST",
          data: { data },
        });
        if (res.code === 200) {
          ElMessage.success("已保存");
          this.editorVisible = false;
          this.load();
        }
      } finally {
        this.saving = false;
      }
    },
    openRun(w) {
      const url = this.$router.resolve("/workbench/run/" + w.id).href;
      window.open(url, "_blank");
    },
    toggleShare(w) {
      const open = !w.isPublic;
      const action = open ? "开启" : "关闭";
      ElMessageBox.confirm(
        open
          ? "公开后，其他人打开链接可只读查看（看不到你的数据）。"
          : "关闭后，之前的分享链接将失效。",
        action + "公开分享",
        { confirmButtonText: "确定", cancelButtonText: "取消" }
      )
        .then(async () => {
          const data = await this.$aesEncrypt({ id: w.id, isPublic: open });
          const res = await this.$axios({
            url: "user/workbench/share",
            method: "POST",
            data: { data },
          });
          if (res.code === 200) {
            const token = res.data && res.data.shareToken;
            const link =
              window.location.origin +
              "/workbench/run/" +
              w.id +
              (token ? "?s=" + token : "");
            this.load();
            if (open && token) {
              ElMessageBox.alert(link, "分享链接（只读）", {
                confirmButtonText: "知道了",
              });
            } else {
              ElMessage.success("已关闭公开分享");
            }
          }
        })
        .catch(() => {});
    },
    removeItem(w) {
      ElMessageBox.confirm(
        `确定删除「${w.name}」？工作台内容与你的数据都会被清除。`,
        "删除工作台",
        { confirmButtonText: "删除", cancelButtonText: "取消", type: "warning" }
      )
        .then(async () => {
          const data = await this.$aesEncrypt({ id: w.id });
          const res = await this.$axios({
            url: "user/workbench/remove",
            method: "POST",
            data: { data },
          });
          if (res.code === 200) {
            ElMessage.success("已删除");
            this.load();
          }
        })
        .catch(() => {});
    },
    fmtTime(t) {
      if (!t) return "—";
      const d = new Date(t);
      if (Number.isNaN(d.getTime())) return String(t).slice(0, 16);
      const p = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(
        d.getDate()
      )} ${p(d.getHours())}:${p(d.getMinutes())}`;
    },
    fmtSize(n) {
      const size = Number(n) || 0;
      if (size < 1024) return size + " B";
      return (size / 1024).toFixed(1) + " KB";
    },
    /** 找出 HTML 里的外部脚本 / 样式引用，用于提示「不是单文件」 */
    scanExternal(html) {
      const found = [];
      const patterns = [
        /<script[^>]+src=["']([^"']+)["']/gi,
        /<link[^>]+rel=["']stylesheet["'][^>]*href=["']([^"']+)["']/gi,
        /<link[^>]+href=["']([^"']+)["'][^>]*rel=["']stylesheet["']/gi,
        /@import\s+(?:url\()?["']([^"')]+)["']/gi,
      ];
      patterns.forEach((re) => {
        let m;
        while ((m = re.exec(html)) !== null) {
          const url = (m[1] || "").trim();
          if (url && !url.startsWith("data:") && !url.startsWith("#")) {
            found.push(url);
          }
        }
      });
      return Array.from(new Set(found));
    },
    /** 内置示例：纯 CSS 看板，演示 window.WB 读写 */
    sampleHtml() {
      return [
        "<!DOCTYPE html>",
        '<html lang="zh-CN">',
        "<head>",
        '<meta charset="utf-8">',
        '<meta name="viewport" content="width=device-width, initial-scale=1">',
        "<title>我的工作台</title>",
        "<style>",
        ":root{--wb-accent:#3b82f6}",
        "*{box-sizing:border-box}",
        'body{margin:0;font-family:-apple-system,"PingFang SC","Microsoft YaHei",sans-serif;background:#f7f8fa;color:#111827}',
        ".wrap{display:flex;min-height:100vh}",
        ".side{width:180px;background:#fff;border-right:1px solid #eef0f4;padding:16px 12px}",
        ".side h3{font-size:13px;margin:0 0 10px;color:#9ca3af;font-weight:600}",
        ".nav{display:block;padding:8px 10px;border-radius:8px;font-size:14px;color:#374151;cursor:pointer;margin-bottom:4px}",
        ".nav.on{background:rgba(59,130,246,.12);color:var(--wb-accent);font-weight:600}",
        "main{flex:1;padding:20px 24px}",
        ".head{display:flex;align-items:center;justify-content:space-between;margin-bottom:16px}",
        ".head h1{font-size:18px;margin:0}",
        ".btn{background:var(--wb-accent);color:#fff;border:0;border-radius:8px;padding:8px 14px;font-size:13px;cursor:pointer}",
        ".stats{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin-bottom:16px}",
        ".card{background:#fff;border-radius:12px;padding:14px 16px;border:1px solid #eef0f4}",
        ".card b{display:block;font-size:22px}",
        ".card span{font-size:12px;color:#6b7280}",
        ".list{display:grid;grid-template-columns:repeat(auto-fill,minmax(190px,1fr));gap:10px}",
        ".item{background:#fff;border:1px solid #eef0f4;border-radius:10px;padding:12px;font-size:13px;display:flex;justify-content:space-between;align-items:center;gap:8px}",
        ".item .t{cursor:pointer;flex:1}",
        ".item button{border:0;background:transparent;color:#ef4444;cursor:pointer;font-size:12px}",
        'html[data-wb-sidebar="off"] .side{display:none}',
        "</style>",
        "</head>",
        "<body>",
        '<div class="wrap">',
        '<aside class="side">',
        "<h3>功能</h3>",
        '<div class="nav on">任务看板</div>',
        '<div class="nav">我的收藏</div>',
        "</aside>",
        "<main>",
        '<div class="head"><h1>我的工作台</h1><button class="btn" id="add">新增事项</button></div>',
        '<div class="stats"></div>',
        '<div class="list" id="list"></div>',
        "</main>",
        "</div>",
        "<script>",
        "(function(){",
        "  function start(){",
        "  var list = (window.WB && window.WB.get('items')) || [];",
        "  function save(){ if(window.WB) window.WB.set('items', list); }",
        "  function render(){",
        "    document.querySelector('.stats').innerHTML =",
        "      '<div class=\"card\"><b>'+list.length+'</b><span>全部事项</span></div>'+",
        "      '<div class=\"card\"><b>'+list.filter(function(i){return !i.done;}).length+'</b><span>待办</span></div>'+",
        "      '<div class=\"card\"><b>'+list.filter(function(i){return i.done;}).length+'</b><span>已完成</span></div>';",
        "    var box = document.getElementById('list');",
        "    if(!list.length){ box.innerHTML = '<div class=\"card\">还没有事项，点右上角新增</div>'; return; }",
        "    box.innerHTML = list.map(function(i,idx){",
        "      return '<div class=\"item\"><span class=\"t\" data-i=\"'+idx+'\" style=\"'+(i.done?'text-decoration:line-through;color:#9ca3af':'')+'\">'+i.text+'</span><button data-d=\"'+idx+'\">删</button></div>';",
        "    }).join('');",
        "    box.querySelectorAll('.t').forEach(function(el){ el.onclick = function(){ list[+el.dataset.i].done = !list[+el.dataset.i].done; save(); render(); }; });",
        "    box.querySelectorAll('button[data-d]').forEach(function(el){ el.onclick = function(){ list.splice(+el.dataset.d,1); save(); render(); }; });",
        "  }",
        "  document.getElementById('add').onclick = function(){",
        "    var t = prompt('事项内容'); if(!t) return; list.push({text:t,done:false}); save(); render();",
        "  };",
        "  render();",
        "  }",
        "  if(window.WB && window.WB.onReady){ window.WB.onReady(start); } else { start(); }",
        "})();",
        "<" + "/script>",
        "</body>",
        "</html>",
      ].join("\n");
    },
  },
};
</script>
